import {
  ComponentType,
  Dispatch,
  lazy,
  LazyExoticComponent,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';

export const ProgramGlobalViewComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramGlobalViewBody/BroadcastScheduleProgramGlobalViewBody'
    )
);

import { TClip } from '@/features/clips/types';
import { TSegment } from '@/features/segments/types';

import {
  EClipToolkit,
  ESegmentToolkit,
} from '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { TVisibleBroadcastProgramCards } from '../context/BroadcastScheduleProgramContext';
import { TToolkitComponentProps } from '../types';

const LogotiposComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/BroadcastScheduleProgramBodySegmentToolkitLogotipos'
    )
);
const LogotiposClipComponent = lazy(
  () =>
    import(
      '../../clips/components/ClipToolkit/components/ClipToolkitLogotipos/ClipToolkitLogotipos'
    )
);
const PeopleComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitPeople/BroadcastScheduleProgramBodySegmentToolkitPeople'
    )
);
const PeopleClipComponent = lazy(
  () => import('../../clips/components/ClipToolkit/components/ClipToolkitPeople/ClipToolkitPeople')
);

const RotuloComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitRotulo/BroadcastScheduleProgramBodySegmentToolkitRotulo'
    )
);

const MentionComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitMention/BroadcastScheduleProgramBodySegmentToolkitMention'
    )
);
const TimeKeepingComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitTimeKeeping/BroadcastScheduleProgramBodySegmentToolkitTimeKeeping'
    )
);
const CutComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitCuts/BroadcastScheduleProgramBodySegmentToolkitCut'
    )
);
const InfoComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitAditionalInfo/BroadcastScheduleProgramBodySegmentToolkitAditionalInfo'
    )
);
const InfoClipComponent = lazy(
  () =>
    import('../../clips/components/ClipToolkit/components/ClipAdditionalInfo/ClipAdditionalInfo')
);
const DeclarationComponent = lazy(
  () =>
    import(
      '../components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitDeclarations/BroadcastScheduleProgramBodySegmentToolkitDeclarations'
    )
);
const DeclarationClipComponent = lazy(
  () =>
    import(
      '../../clips/components/ClipToolkit/components/ClipToolkitDeclarations/ClipToolkitDeclarations'
    )
);
const RotulosClipComponent = lazy(
  () =>
    import(
      '../../clips/components/ClipToolkit/components/ClipRotulosComponent/ClipRotulosComponent'
    )
);
const MentionsClipComponent = lazy(
  () =>
    import('../../clips/components/ClipToolkit/components/ClipToolkitMention/ClipToolkitMentions')
);
const CutClipComponent = lazy(
  () => import('../../clips/components/ClipToolkit/components/ClipCut/ClipCut')
);

type TUseToolkitStateProps = {
  activeSegment: TSegment | null;
  activeClip: TClip | null;
  isParrillaSegments: boolean;
  isGlobalView: boolean;
  setVisibleBroadcastProgramCards: Dispatch<SetStateAction<TVisibleBroadcastProgramCards>>;
};

type TUseToolkitStateReturn = {
  hoveredId: ESegmentToolkit | null;
  pinnedId: ESegmentToolkit | null;
  toolkitComponents: Record<
    ESegmentToolkit,
    LazyExoticComponent<ComponentType<TToolkitComponentProps>>
  >;
  toolkitClipComponents: Record<
    EClipToolkit,
    LazyExoticComponent<ComponentType<TToolkitComponentProps>>
  >;
  handleMouseEnter: (id: ESegmentToolkit) => void;
  handleMouseLeave: () => void;
  handleTooltikClick: (id: ESegmentToolkit) => void;
};

export function useToolkitState({
  activeSegment,
  activeClip,
  isParrillaSegments,
  isGlobalView,
  setVisibleBroadcastProgramCards,
}: TUseToolkitStateProps): TUseToolkitStateReturn {
  const [hoveredId, setHoveredId] = useState<ESegmentToolkit | null>(null);
  const [pinnedId, setPinnedId] = useState<ESegmentToolkit | null>(ESegmentToolkit.LOGOTIPO);

  const toolkitComponents: Record<
    ESegmentToolkit,
    LazyExoticComponent<ComponentType<TToolkitComponentProps>>
  > = useMemo(() => {
    return {
      [ESegmentToolkit.TIMEKEEPING]: TimeKeepingComponent,
      [ESegmentToolkit.CORTES]: CutComponent,
      [ESegmentToolkit.LOGOTIPO]: LogotiposComponent,
      [ESegmentToolkit.PERSONAS]: PeopleComponent,
      [ESegmentToolkit.DECLARACIONES]: DeclarationComponent,
      [ESegmentToolkit.ROTULOS]: RotuloComponent,
      [ESegmentToolkit.MENCIONES]: MentionComponent,
      [ESegmentToolkit.INFORMACION]: InfoComponent,
    };
  }, []);

  const toolkitClipComponents: Record<
    EClipToolkit,
    LazyExoticComponent<ComponentType<TToolkitComponentProps>>
  > = useMemo(() => {
    return {
      [ESegmentToolkit.TIMEKEEPING]: TimeKeepingComponent,
      [ESegmentToolkit.CORTES]: CutClipComponent,
      [ESegmentToolkit.LOGOTIPO]: LogotiposClipComponent,
      [ESegmentToolkit.PERSONAS]: PeopleClipComponent,
      [ESegmentToolkit.DECLARACIONES]: DeclarationClipComponent,
      [ESegmentToolkit.ROTULOS]: RotulosClipComponent,
      [ESegmentToolkit.MENCIONES]: MentionsClipComponent,
      [ESegmentToolkit.INFORMACION]: InfoClipComponent,
    };
  }, []);

  const handleMouseEnter = (id: ESegmentToolkit) => {
    if (pinnedId !== id && hoveredId !== id) setHoveredId(id);
  };

  const handleMouseLeave = () => {
    setHoveredId(null);
  };

  const handleTooltikClick = (id: ESegmentToolkit) => {
    if (pinnedId !== id) {
      setPinnedId(id);
      setHoveredId(null);
    }
  };

  useEffect(() => {
    if ((activeSegment || activeClip) && pinnedId && isParrillaSegments) {
      setVisibleBroadcastProgramCards(prev => {
        return {
          ...prev,
          segmentDetail: true,
        };
      });
    }
  }, [pinnedId]);

  useEffect(() => {
    if (!activeSegment && !isGlobalView) {
      setPinnedId(null);
      setHoveredId(null);
    }
  }, [activeSegment, isGlobalView]);

  return {
    hoveredId,
    pinnedId,
    toolkitComponents,
    toolkitClipComponents,
    handleMouseEnter,
    handleMouseLeave,
    handleTooltikClick,
  };
}
